import axios from 'axios';

// Get API base URL from WordPress BOAT_TRADER_API global
const getApiBaseURL = (): string => {
  if (typeof window === 'undefined') return '';
  
  // First check BOAT_TRADER_API.rest_url - this is the most reliable source
  const boatTraderApi = (window as any).BOAT_TRADER_API;
  if (boatTraderApi && boatTraderApi.rest_url) {
    const url = boatTraderApi.rest_url.replace(/\/$/, '');
    return url;
  }
  
  // Fallback: Try to detect from WordPress global
  if (typeof window !== 'undefined' && (window as any).wpApiSettings?.root) {
    const wpRoot = (window as any).wpApiSettings.root;
    if (wpRoot.includes('boat-trader-v3')) {
      return wpRoot.replace(/\/$/, '') + '/boat-trader-v3/v1';
    }
  }
  
  // Last resort fallback: construct from current location
  const hostname = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';
  const path = window.location.pathname.includes('/wordpress/') ? '/wordpress' : '';
  const fallbackUrl = `${window.location.protocol}//${hostname}${port}${path}/wp-json/boat-trader-v3/v1`;
  return fallbackUrl;
};

const getToken = () => {
  if (typeof window === 'undefined') return null;
  let token = (window as any).boat_trader_token;
  // Fallback to localStorage if window token is missing (e.g. after page refresh)
  if (!token || token === 'undefined' || token === 'null') {
    try {
      token = localStorage.getItem('boat_trader_token');
    } catch {
      // localStorage may be unavailable
    }
  }
  if (!token || token === 'undefined' || token === 'null') return null;
  return token as string | null;
};

const api = axios.create({
  baseURL: getApiBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

  api.interceptors.request.use((config) => {
    // B2B data freshness: add timestamp to every GET request to bypass ALL caches
    // (Service Worker bt-api cache, browser cache, proxy cache, LiteSpeed cache).
    // This ensures every API request hits the server for fresh data.
    if (config.method?.toLowerCase() === 'get' && config.params) {
      config.params._t = Date.now();
    } else if (config.method?.toLowerCase() === 'get') {
      config.params = { _t: Date.now() };
    }

    const token = getToken();
    // Send token as both Authorization (standard) and custom header (fallback for server configs that strip Authorization)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['X-Boat-Token'] = token;
    } else if (config.headers && 'Authorization' in config.headers) {
      delete config.headers['Authorization'];
    }
    
// Set X-WP-Nonce for cookie-based WordPress authentication
      // Without this, is_user_logged_in() returns false in REST API context
      // SKIP per route pubbliche: login usa nonce stale dopo logout → 403 rest_cookie_invalid_nonce
      const skipNoncePaths = ['/auth/login', '/auth/register', '/auth/check-email', '/items/', '/boats', '/motors', '/accessories', '/map/', '/radius-counts', '/radius-steps'];
      const shouldSkipNonce = skipNoncePaths.some(p => config.url?.includes(p));
    if (!shouldSkipNonce) {
      const nonce = (window as any).BOAT_TRADER_API?.nonce;
      if (nonce && config.headers) {
        config.headers['X-WP-Nonce'] = nonce;
      }
    }
    
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    if (config.url?.includes('/auth/login')) {
      console.log('[API REQUEST] Login request:', { url: config.url, baseURL: config.baseURL, method: config.method, hasToken: !!token, contentType: config.headers?.['Content-Type'] });
    }
    return config;
  });

// Response interceptor for error handling
  api.interceptors.response.use(
    (response) => {
      if (response.config?.url?.includes('/auth/login')) {
        console.log('[API RESPONSE] Login success:', { status: response.status, hasData: !!response.data });
      }
      return response;
    },
    (error) => {
      if (error.config?.url?.includes('/auth/login')) {
        console.error('[API RESPONSE] Login FAILED:', { status: error.response?.status, data: error.response?.data, message: error.message, code: error.code });
      }
      if (error.response?.status === 401) {
        console.warn('[API] 401 Unauthorized:', error.config?.url || 'unknown');
        const token = getToken();
        // Note: token cleanup is handled by auth.tsx logout; no localStorage here
      }
      return Promise.reject(error);
    }
  );

// Export function to clear auth header (for logout)
export const clearAuthHeader = () => {
  delete api.defaults.headers.common['Authorization'];
};

export const authApi = {
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Export getApiBaseURL for use in other components
export { getApiBaseURL };

export const reservationApi = {
  reserve: (boatId: number) => api.post(`/boats/${boatId}/reserve`),
  cancel: (boatId: number) => api.delete(`/boats/${boatId}/reserve`),
  getStatus: (boatId: number) => api.get(`/boats/${boatId}/reservation`),
  extend: (boatId: number, extendHours: number = 24) => api.post(`/boats/${boatId}/extend-reservation`, { extend_hours: extendHours }),
};

export const historyApi = {
  getBoatHistory: (boatId: number) => api.get(`/boats/${boatId}/history`),
  getPriceHistory: (boatId: number) => api.get(`/boats/${boatId}/price-history`),
};

export const imageApi = {
  // Upload single image
  uploadImage: async (boatId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    if (boatId > 0) {
      formData.append('boat_id', boatId.toString());
    }
    
    const response = await api.post(`/boats/${boatId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Upload multiple images
  uploadMultiple: async (boatId: number, files: FileList | File[]) => {
    const formData = new FormData();
    
    if (boatId > 0) {
      formData.append('boat_id', boatId.toString());
    }
    
    // Handle both FileList and array
    // Use 'image[]' without index - PHP will automatically create the array structure
    const fileArray = files instanceof FileList ? Array.from(files) : files;
    fileArray.forEach((file) => {
      formData.append('image[]', file);
    });
    
    const response = await api.post(`/boats/${boatId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Upload base64 encoded image
  uploadBase64: async (boatId: number, base64Data: string) => {
    const response = await api.post(`/boats/${boatId}/images`, 
      { image: base64Data },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response;
  },

  // Delete image
  deleteImage: async (attachmentId: number) => {
    const response = await api.delete(`/images/${attachmentId}`);
    return response;
  },

  // Set boat images (replace all)
  setBoatImages: async (boatId: number, imageIds: number[]) => {
    const response = await api.put(`/boats/${boatId}/images`, {
      image_ids: imageIds,
    });
    return response;
  },

  // Reorder boat images
  reorderImages: async (boatId: number, imageUrls: string[]) => {
    console.log('[API] reorderImages called:', { boatId, imageUrls });
    const response = await api.put(`/boats/${boatId}/images/reorder`, {
      images: imageUrls,
    });
    console.log('[API] reorderImages response:', response.data);
    return response;
  },

  // Update boat
  updateBoat: async (boatId: number, data: any) => {
    const response = await api.put(`/boats/${boatId}`, data);
    return response;
  },

  // Get boat for editing
  getBoat: async (boatId: number) => {
    const response = await api.get(`/boats/${boatId}`);
    return response;
  },
};

export const chatApi = {
  getRooms: () => api.get('/chat/rooms'),
  getDealers: () => api.get('/dealers'),
  getRoom: (roomId: number) => api.get(`/chat/rooms/${roomId}`),
  createRoom: (participantId: number, tradeId?: number) =>
    api.post('/chat/rooms', { participantId, tradeId }),
  getMessages: (roomId: number, limit?: number) =>
    api.get(`/chat/rooms/${roomId}/messages`, { params: { limit } }),
  sendMessage: (roomId: number, message: string) =>
    api.post(`/chat/rooms/${roomId}/messages`, { message }),
  markAsRead: (roomId: number) =>
    api.put(`/chat/rooms/${roomId}/read`),
  getUnreadCount: () => api.get('/chat/unread'),
};

// Dealer API
export const dealerApi = {
  // Get all dealers
  getDealers: () => api.get('/dealers'),
  
  // Get single dealer
  getDealer: (id: number) => api.get(`/dealers/${id}`),
  
  // Update dealer profile (dealer updates their own profile)
  updateDealer: (id: number, data: any) => api.put(`/dealers/${id}`, data),
  
  // Admin: Get all dealers with all fields
  getAdminDealers: () => api.get('/admin/dealers'),
  
  // Admin: Update any dealer
  updateAdminDealer: (id: number, data: any) => api.put(`/admin/dealers/${id}`, data),
};

// Cities API - uses axios for consistent interceptor handling
const CITIES_API_URL = () => {
  const base = getApiBaseURL();
  return base + '/cities';
};

export const citiesApi = {
  getCities: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await api.get(CITIES_API_URL(), { params });
    return response.data;
  },
  getCityById: async (id: number) => {
    const response = await api.get(CITIES_API_URL(), { params: { id } });
    return response.data;
  },
  getCitiesByProvince: async (province: string) => {
    const response = await api.get(CITIES_API_URL(), { params: { province } });
    return response.data;
  },
  getCitiesByRegion: async (region: string) => {
    const response = await api.get(CITIES_API_URL(), { params: { region } });
    return response.data;
  },
  getProvinces: async () => {
    const response = await api.get(CITIES_API_URL(), { params: { provinces: 1 } });
    return response.data;
  },
  getRegions: async () => {
    const response = await api.get(CITIES_API_URL(), { params: { regions: 1 } });
    return response.data;
  },
};

export const csvApi = {
  // Download CSV template
  getTemplate: async (): Promise<{ data: { data: string; filename: string } }> => {
    const response = await api.get('/boats/csv/template');
    return response;
  },

  // Upload CSV for import
  importCSV: async (
    file: File,
    updateExisting: boolean = false
  ): Promise<{
    data: {
      data: {
        imported: number;
        updated: number;
        errors: string[];
        warnings: string[];
        total: number;
      };
    };
  }> => {
    const formData = new FormData();
    formData.append('csv_file', file);
    formData.append('update_existing', updateExisting.toString());

    const response = await api.post('/boats/csv/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Export boats to CSV
  exportCSV: async (filters?: {
    status?: string;
    brand?: string;
    minPrice?: number;
    maxPrice?: number;
    limit?: number;
  }): Promise<{
    data: {
      data: string;
      filename: string;
      count: number;
    };
  }> => {
    const response = await api.get('/boats/csv/export', {
      params: filters,
    });
    return response;
  },

  // Download exported CSV file
  downloadExport: async (filters?: {
    status?: string;
    brand?: string;
    minPrice?: number;
    maxPrice?: number;
    limit?: number;
  }): Promise<void> => {
    const response = await api.get('/boats/csv/export', {
      params: filters,
      responseType: 'blob',
    });

    // Handle both axios response formats
    const blob = response.data instanceof Blob 
      ? response.data 
      : new Blob([response.data], { type: 'text/csv' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from content-disposition header or use default
    const contentDisposition = response.headers?.['content-disposition'];
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    link.download = filenameMatch?.[1] || 'boats_export.csv';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// Trades API
export const tradesApi = {
  // Get all trades for current user
  getTrades: () => api.get('/trades'),
  
  // Get single trade by ID
  getTrade: (id: number) => api.get(`/trades/${id}`),
  
  // Create new trade
  createTrade: (data: {
    boat_id: number;
    seller_id: number;
    proposed_price: number;
    message?: string;
  }) => api.post('/trades', data),
  
  // Update trade (price, message)
  updateTrade: (id: number, data: {
    proposed_price?: number;
    message?: string;
  }) => api.put(`/trades/${id}`, data),
  
  // Delete trade
  deleteTrade: (id: number) => api.delete(`/trades/${id}`),
  
  // Get trade messages
  getMessages: (tradeId: number) => api.get(`/trades/${tradeId}/messages`),
  
  // Add trade message
  addMessage: (tradeId: number, message: string) => 
    api.post(`/trades/${tradeId}/messages`, { message }),
  
  // Update trade status
  updateStatus: (tradeId: number, status: string) => 
    api.put(`/trades/${tradeId}/status`, { status }),
};

// ===== UNIFIED ITEMS API =====
// This is the future-proof solution - single API for all item types
// Usage: itemsApi.getItems('boats', params) or itemsApi.getItems('motors', params)
export const itemsApi = {
  // Get items by type: 'boats' or 'motors' (accessories coming soon)
  // Use getItemsPublic for unauthenticated access
  getItems: (type: string, params?: Record<string, any>) => 
    api.get(`/items/${type}`, { params }),
  
  // Public access - returns retail prices only, no dealer prices
  getItemsPublic: (type: string, params?: Record<string, any>) => 
    api.get(`/${type}/public`, { params }),
  getMapItems: (params?: Record<string, any>) =>
    api.get('/map/items', { params }),
  // Search map items with filters
  getMapSearchItems: (params?: Record<string, any>) =>
    api.get('/map/search-items', { params }),
  getMapBootstrap: () =>
    api.get('/map/bootstrap'),
  getMapRingItems: (params?: Record<string, any>) =>
    api.get('/map/ring-items', { params }),
  
  // Get single item by type and ID
  getItem: (type: string, id: number) => 
    api.get(`/items/${type}/${id}`),
  
  // Count boats by radius - per filtri km dinamici
  getBoatsCountByRadius: (params: { lat: number; lng: number; radius?: number; filters?: Record<string, any> }) =>
    api.get('/boats/count-by-radius', { params }),
  
  // Count motors by radius - per filtri km dinamici
  getMotorsCountByRadius: (params: { lat: number; lng: number; radius?: number; filters?: Record<string, any> }) =>
    api.get('/motors/count-by-radius', { params }),
  
// Get radius counts for ALL bands in ONE call - MUCH FASTER!
    getRadiusCounts: (type: 'boats' | 'motors' | 'accessories', params: { lat: number; lng: number; status?: string; available_only?: boolean; search?: string; brand?: string; model?: string; min_price?: number; max_price?: number }) =>
       api.get(`/${type}/radius-counts`, { params }),
   
  // Get dynamic radius steps from API
  getRadiusSteps: (type: 'boats' | 'motors' | 'accessories', params: { lat: number; lng: number; search?: string; brand?: string; model?: string; min_price?: number; max_price?: number }) =>
    api.get(`/${type}/radius-steps`, { params }),
};

// Motors API
export const motorApi = {
  // Get all motors with filters
  getMotors: (params?: {
    search?: string;
    brand?: string;
    model?: string;
    min_price?: number;
    max_price?: number;
    year?: number;
    min_hours?: number;
    max_hours?: number;
    engine_type?: string;
    fuel_type?: string;
    condition?: string;
    horsepower?: number;
    lat?: number;
    lng?: number;
    radius_km?: number;
    sort_by?: string;
  }) => api.get('/motors', { params }),

  // Get single motor
  getMotor: (id: number) => api.get(`/motors/${id}`),

  // Create motor
  createMotor: (data: any) => api.post('/motors', data),

  // Update motor
  updateMotor: (id: number, data: any) => api.put(`/motors/${id}`, data),

  // Delete motor
  deleteMotor: (id: number) => api.delete(`/motors/${id}`),

  // Get my motors
  getMyMotors: () => api.get('/motors/my-motors'),

  // Get motors brands
  getBrands: () => api.get('/motors/brands'),

  // Get motors models
  getModels: (brand?: string) => api.get('/motors/models', { params: { brand } }),

  // Get filters options
  getFiltersOptions: () => api.get('/motors/filters/options'),

  // Get price range
  getPriceRange: () => api.get('/motors/filters/price-range'),

  // Reservation
  reserve: (motorId: number) => api.post(`/motors/${motorId}/reserve`),
  cancelReservation: (motorId: number) => api.delete(`/motors/${motorId}/reserve`),
  getReservation: (motorId: number) => api.get(`/motors/${motorId}/reservation`),

  // Reorder motor images
  reorderImages: async (motorId: number, imageUrls: string[]) => {
    const response = await api.put(`/motors/${motorId}/images/reorder`, {
      images: imageUrls,
    });
    return response;
  },
};

// Motor Images API
export const motorImageApi = {
  // Upload single image
  uploadImage: async (motorId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    if (motorId > 0) {
      formData.append('motor_id', motorId.toString());
    }
    
    const response = await api.post(`/motors/${motorId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Upload multiple images
  uploadMultiple: async (motorId: number, files: FileList | File[]) => {
    const formData = new FormData();
    
    if (motorId > 0) {
      formData.append('motor_id', motorId.toString());
    }
    
    const fileArray = files instanceof FileList ? Array.from(files) : files;
    fileArray.forEach((file) => {
      formData.append('image[]', file);
    });
    
    const response = await api.post(`/motors/${motorId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Set motor images (replace all)
  setMotorImages: async (motorId: number, imageIds: number[]) => {
    const response = await api.put(`/motors/${motorId}/images`, {
      image_ids: imageIds,
    });
    return response;
  },
};

// Boat Video API
export const boatVideoApi = {
  uploadVideo: async (boatId: number, file: File) => {
    const formData = new FormData();
    formData.append('video', file);
    if (boatId > 0) {
      formData.append('boat_id', boatId.toString());
    }
    
    const response = await api.post(`/boats/${boatId}/videos/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },
  
  setVideos: async (boatId: number, videos: any[]) => {
    const response = await api.put(`/boats/${boatId}/videos`, {
      videos: videos,
    });
    return response;
  },
};

  // Motor Video API
export const motorVideoApi = {
  uploadVideo: async (motorId: number, file: File) => {
    const formData = new FormData();
    formData.append('video', file);
    if (motorId > 0) {
      formData.append('motor_id', motorId.toString());
    }
    
    const response = await api.post(`/motors/${motorId}/videos/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },
  
  setVideos: async (motorId: number, videos: any[]) => {
    const response = await api.put(`/motors/${motorId}/videos`, {
      videos: videos,
    });
    return response;
  },
};

// Accessory API
export const accessoryApi = {
  // Get all accessories
  getAccessories: (params?: {
    search?: string;
    brand?: string;
    model?: string;
    min_price?: number;
    max_price?: number;
    year?: number;
    category?: string;
    condition?: string;
    lat?: number;
    lng?: number;
    radius_km?: number;
    sort_by?: string;
  }) => api.get('/accessories', { params }),

  // Get single accessory
  getAccessory: (id: number) => api.get(`/accessories/${id}`),

  // Create accessory
  createAccessory: (data: any) => api.post('/accessories', data),

  // Update accessory
  updateAccessory: (id: number, data: any) => api.put(`/accessories/${id}`, data),

  // Delete accessory
  deleteAccessory: (id: number) => api.delete(`/accessories/${id}`),

  // Get my accessories
  getMyAccessories: () => api.get('/accessories/my-accessories'),

  // Get filters options
  getFiltersOptions: () => api.get('/accessories/filters/options'),

  // Get price range
  getPriceRange: () => api.get('/accessories/filters/price-range'),

  // Reservation
  reserve: (accessoryId: number) => api.post(`/accessories/${accessoryId}/reserve`),
  cancelReservation: (accessoryId: number) => api.delete(`/accessories/${accessoryId}/reserve`),
  getReservation: (accessoryId: number) => api.get(`/accessories/${accessoryId}/reservation`),

  // Reorder accessory images
  reorderImages: async (accessoryId: number, imageUrls: string[]) => {
    const response = await api.put(`/accessories/${accessoryId}/images/reorder`, {
      images: imageUrls,
    });
    return response;
  },
};

// Accessory Images API
export const accessoryImageApi = {
  // Upload single image
  uploadImage: async (accessoryId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    if (accessoryId > 0) {
      formData.append('accessory_id', accessoryId.toString());
    }
    
    const response = await api.post(`/accessories/${accessoryId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Upload multiple images
  uploadMultiple: async (accessoryId: number, files: FileList | File[]) => {
    const formData = new FormData();
    
    if (accessoryId > 0) {
      formData.append('accessory_id', accessoryId.toString());
    }
    
    const fileArray = files instanceof FileList ? Array.from(files) : files;
    fileArray.forEach((file) => {
      formData.append('image[]', file);
    });
    
    const response = await api.post(`/accessories/${accessoryId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Set accessory images (replace all)
  setAccessoryImages: async (accessoryId: number, imageIds: number[]) => {
    const response = await api.put(`/accessories/${accessoryId}/images`, {
      image_ids: imageIds,
    });
    return response;
  },
};

export default api;

